import { Alert, Box, Button, CircularProgress, createTheme, CssBaseline, Paper, TextField, ThemeProvider, Typography } from "@mui/material";
import React, { useState } from "react";
import { registerWithEmail, signInWithEmail } from "../services/firebase";
import { getCurrentEnvironmentName, shouldShowEnvironmentName } from "../utils/environment";
import { PasswordReset } from "./PasswordReset";

// Create a clean theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#2c3e50",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

interface AuthFormProps {
  onAuthSuccess: () => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please fill in all required fields");
      return;
    }

    if (!isLogin && !displayName) {
      setError("Please enter your display name");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (isLogin) {
        await signInWithEmail(email, password);
        onAuthSuccess();
      } else {
        await registerWithEmail(email, password, displayName);
        // Registration successful - user will be redirected to main app
        onAuthSuccess();
      }
    } catch (error: unknown) {
      // Handle common Firebase Auth errors
      let errorMessage = "Authentication failed";
      if (error && typeof error === "object" && "code" in error) {
        const firebaseError = error as { code: string; message?: string };
        if (firebaseError.code === "auth/user-not-found") {
          errorMessage = "No account found with this email address";
        } else if (firebaseError.code === "auth/wrong-password") {
          errorMessage = "Incorrect password";
        } else if (firebaseError.code === "auth/invalid-credential") {
          errorMessage = "There's something wrong with the email or password";
        } else if (firebaseError.code === "auth/email-already-in-use") {
          errorMessage = "An account with this email already exists";
        } else if (firebaseError.code === "auth/weak-password") {
          errorMessage = "Password should be at least 6 characters";
        } else if (firebaseError.code === "auth/invalid-email") {
          errorMessage = "Please enter a valid email address";
        } else if (firebaseError.message === "EMAIL_NOT_VERIFIED") {
          // Let App.tsx handle unverified email flow
          errorMessage = "Please verify your email address before signing in. Check your inbox for a verification email.";
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError(null);
    setEmail("");
    setPassword("");
    setDisplayName("");
  };

  // Show password reset form
  if (showPasswordReset) {
    return (
      <PasswordReset
        onBack={() => {
          setShowPasswordReset(false);
          setIsLogin(true);
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 2,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Paper
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 400,
          }}
        >
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {isLogin ? "Sign In" : "Create Account"}
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {isLogin ? "Welcome back! Please sign in to your account." : "Create a new account to get started."}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {!isLogin && <TextField fullWidth label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} margin="normal" required disabled={isLoading} />}

            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required disabled={isLoading} />

            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required disabled={isLoading} />

            <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading} sx={{ mt: 3, mb: 2 }}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Button variant="text" onClick={toggleMode} disabled={isLoading} sx={{ textTransform: "none" }}>
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </Button>
          </Box>

          {isLogin && (
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Button variant="text" onClick={() => setShowPasswordReset(true)} disabled={isLoading} sx={{ textTransform: "none", fontSize: "0.875rem" }}>
                Forgot your password?
              </Button>
            </Box>
          )}

          {/* Display environment name in non-production environments */}
          {shouldShowEnvironmentName() && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Environment: {getCurrentEnvironmentName()}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};
